/**
 * Alerts — the pure core of Phase 7.
 *
 * NO CLOCK, NO I/O, NO RANDOMNESS. Rules and events in, decisions out. The worker
 * host owns every side effect; everything that decides whether a human is disturbed
 * lives here, where it can be tested exhaustively and replayed.
 *
 * ─── THE TWO WAYS THIS MODULE CAN DESTROY THE PRODUCT ────────────────────────
 *
 *   CRIED WOLF — forty notifications for one flapping condition. The manager mutes
 *   RIVENA, and three weeks later misses the crash alert. The mute is permanent;
 *   nobody un-mutes an alarm system that lied to them.
 *
 *   STAYED SILENT — the crash alert that never left the building. There is no
 *   recovery from that one either.
 *
 * Every guard below is one of those two, and they pull in opposite directions. Where
 * they conflict the tie is broken by consequence: a suppressed `ignition_on` costs
 * nobody anything, a suppressed `crash` costs someone an ambulance. So the cooldown,
 * the rate limit and the dedupe key are uniform mechanisms with per-type DEFAULTS
 * that differ by two orders of magnitude — see `ALERT_DEFAULT_COOLDOWN_S`.
 *
 * ─── DECISIONS ARE MADE ON THE EVENT'S OWN TIME, NEVER ON ARRIVAL TIME ───────
 *
 * The whole platform follows "never trust arrival time" and this module is where it
 * bites hardest. Cooldown, rate limit and schedule are all evaluated against
 * `occurredAtMs` — the record's own timestamp — so a device that buffered a night and
 * flushed it at 08:00 produces the same alerts as one that reported live, and a
 * re-run over the same window reaches the same verdict. A cooldown measured against
 * `Date.now()` would collapse a whole flushed night into one alert, which is exactly
 * the silence half of the failure.
 *
 * ─── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 *
 * Geofence geometry. Rule 5 says in/out is a PostGIS predicate over consecutive
 * fixes, and re-implementing point-in-polygon in TypeScript would give the platform
 * two answers to one question. This module consumes a transition that the database
 * already decided (`AlertEvent.geofenceId` plus `previousInside`), and refuses to
 * fabricate one — see `geofenceTransition`.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * Vocabularies
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Three levels, not five. A severity scale nobody can apply consistently degrades
 * into "everything is high", and the only distinction that changes behaviour is:
 * does this go in a digest (`info`), does it interrupt the working day (`warning`),
 * or does it wake somebody up (`critical`).
 */
export const ALERT_SEVERITIES = ['info', 'warning', 'critical'];
const SEVERITY_RANK = {
    info: 0,
    warning: 1,
    critical: 2,
};
export function severityAtLeast(value, floor) {
    return SEVERITY_RANK[value] >= SEVERITY_RANK[floor];
}
/**
 * Rule 3, as a stored fact rather than a convention.
 *
 * `device` means the FMC150 raised it — the accelerometer decided it was a crash,
 * the modem decided it was being jammed. `platform` means RIVENA derived it from
 * a series of records. The two are not the same evidence and a support conversation
 * about a false positive begins by asking which one it was, so the value is on the
 * row and it is NOT NULL.
 */
export const ALERT_SOURCES = ['device', 'platform'];
/**
 * The whole of spec §8.7, in one list.
 *
 * `driver_licence_expiry` is the one addition: Phase 6 stores `drivers.licence_expiry`
 * and the timer sweep is the only thing in the platform that could ever notice it
 * lapsing. A driver whose licence expired last Tuesday is an insurance problem, and
 * it belongs to the same watchdog that produces the offline alerts.
 */
export const ALERT_EVENT_TYPES = [
    'ignition_on',
    'ignition_off',
    /** Movement with the ignition off — the device's own AVL 246, or derived. */
    'towing',
    'geofence_in',
    'geofence_out',
    'overspeed',
    /** Harsh acceleration, braking or cornering. The kind travels in the payload. */
    'harsh_event',
    'crash',
    'jamming',
    'unplug',
    /** External supply lost while the device kept reporting on its internal battery. */
    'power_cut',
    'low_vehicle_battery',
    'low_internal_battery',
    'fuel_drain',
    'fuel_refill',
    'excessive_idling',
    'offline',
    'maintenance_due',
    'route_deviation',
    'unidentified_driver',
    'door_open_in_zone',
    'driver_licence_expiry',
    /**
     * A fitted casing measured BELOW the legal minimum tread.
     *
     * Its own type rather than a shade of `maintenance_due`, and the reason is what the
     * two mean: a vidange 500 km late is planning, while a tyre under 1.6 mm fails
     * inspection and gives an insurer an argument after an accident. They belong in
     * different inboxes, and folding this into the info-severity maintenance stream is
     * how it would be muted along with everything else there.
     *
     * Deliberately ONLY the illegal band. An alert for every casing under 3 mm would fire
     * for a fifth of a working fleet the day it deployed — rule 1's failure at its most
     * expensive, because the noise arrives before anybody has learned to trust the engine.
     * Amber casings are planning, and the tyre listing is where planning happens.
     */
    'tyre_illegal',
    /**
     * A CONFIRMED cold-chain excursion — see `cold-chain.ts` for what "confirmed" costs.
     *
     * Not simply "a probe read above the threshold". A tail-lift open at a delivery and
     * a fridge unit's defrost cycle both do that, several times a day, by design. An
     * alert on the raw reading is forty notifications a tournée, and the first thing an
     * operator does then is mute the rule — including on the day the unit actually dies.
     */
    'temperature_excursion',
    /**
     * The duress button. AVL 236 on an FMC150, wired to a concealed switch.
     *
     * The one alert in this vocabulary raised by a PERSON asking for help, and the only
     * one where a false negative is measured in bodies rather than money. Everything
     * about its defaults follows from that: critical severity, a 60-second cooldown, and
     * exemption from the per-hour ceiling in `shouldFire`.
     *
     * It is deliberately its own type and not a shade of `crash`. A crash dispatches an
     * ambulance to a location; a duress alarm dispatches the police, and it may mean the
     * vehicle must NOT be approached. Merging them would put the second inside a list of
     * the first, which is where it would be read as one.
     */
    'panic',
    /**
     * Engine or coolant temperature at or above the rule's threshold (100 °C floor).
     * Read off the CAN/OBD temperature the tracker already sends; the rule's `minValue`
     * sets the limit, because a truck's normal running temperature is not a car's.
     */
    'engine_overheat',
    /**
     * A vehicle burned noticeably more per 100 km yesterday than its own average over the
     * previous 30 days. Measured litres only (CAN or tracker counter, tank level), never an
     * average-based estimate: comparing an estimate with itself is always 0 %. The value is
     * the excess in percent, so a rule thresholds on "20 % above normal".
     */
    'fuel_overconsumption',
];
/**
 * FR/EN labels for the surfaces that have no i18n catalogue — e-mail bodies, SMS,
 * CSV exports, the API's `locale` responses — following `FUEL_METHOD_LABELS` and the
 * channel registry. The web app renders from its own catalogue behind the parity
 * gate; a French sentence shipped from here would bypass it.
 */
export const ALERT_EVENT_LABELS = {
    ignition_on: { fr: 'Contact mis', en: 'Ignition on' },
    ignition_off: { fr: 'Contact coupé', en: 'Ignition off' },
    towing: { fr: 'Remorquage (mouvement sans contact)', en: 'Towing (movement without ignition)' },
    geofence_in: { fr: 'Entrée en zone', en: 'Geofence entry' },
    geofence_out: { fr: 'Sortie de zone', en: 'Geofence exit' },
    overspeed: { fr: 'Excès de vitesse', en: 'Over-speeding' },
    harsh_event: { fr: 'Conduite brusque', en: 'Harsh driving event' },
    crash: { fr: 'Choc détecté', en: 'Crash detected' },
    jamming: { fr: 'Brouillage GSM', en: 'GSM jamming' },
    unplug: { fr: 'Boîtier débranché', en: 'Device unplugged' },
    power_cut: { fr: 'Coupure d’alimentation', en: 'Power cut' },
    low_vehicle_battery: { fr: 'Batterie véhicule faible', en: 'Low vehicle battery' },
    low_internal_battery: { fr: 'Batterie interne faible', en: 'Low internal battery' },
    fuel_drain: { fr: 'Siphonnage de carburant', en: 'Fuel drain' },
    fuel_refill: { fr: 'Remplissage de carburant', en: 'Fuel refill' },
    excessive_idling: { fr: 'Ralenti excessif', en: 'Excessive idling' },
    offline: { fr: 'Véhicule hors ligne', en: 'Vehicle offline' },
    maintenance_due: { fr: 'Entretien à échéance', en: 'Maintenance due' },
    route_deviation: { fr: 'Écart d’itinéraire', en: 'Route deviation' },
    unidentified_driver: { fr: 'Conducteur non identifié', en: 'Unidentified driver' },
    door_open_in_zone: { fr: 'Porte ouverte en zone', en: 'Door opened in zone' },
    driver_licence_expiry: { fr: 'Permis de conduire expiré', en: 'Driving licence expired' },
    tyre_illegal: { fr: 'Pneu sous le minimum légal', en: 'Tyre below the legal minimum' },
    temperature_excursion: { fr: 'Rupture de chaîne du froid', en: 'Cold-chain excursion' },
    panic: { fr: 'Bouton d’agression', en: 'Duress alarm' },
    engine_overheat: { fr: 'Surchauffe moteur', en: 'Engine overheating' },
    fuel_overconsumption: { fr: 'Surconsommation de carburant', en: 'Fuel overconsumption' },
};
export function alertEventLabel(type, locale) {
    const label = ALERT_EVENT_LABELS[type];
    return locale === 'en' ? label.en : label.fr;
}
/**
 * The severity a rule gets when its author does not choose one.
 *
 * Only `crash` is critical out of the box, and that is the point: if six event types
 * were critical, the word would mean "the platform is excited" rather than "send
 * somebody". `jamming` and `unplug` sit at warning because they are the signature of
 * a theft in progress AND the signature of a garage disconnecting a battery — a
 * tenant that knows which one it has can raise them per rule.
 */
export const ALERT_DEFAULT_SEVERITY = {
    ignition_on: 'info',
    ignition_off: 'info',
    towing: 'warning',
    geofence_in: 'info',
    geofence_out: 'info',
    overspeed: 'warning',
    harsh_event: 'info',
    crash: 'critical',
    jamming: 'warning',
    unplug: 'warning',
    power_cut: 'warning',
    low_vehicle_battery: 'warning',
    low_internal_battery: 'info',
    fuel_drain: 'warning',
    fuel_refill: 'info',
    excessive_idling: 'info',
    offline: 'warning',
    maintenance_due: 'info',
    route_deviation: 'warning',
    unidentified_driver: 'warning',
    door_open_in_zone: 'warning',
    driver_licence_expiry: 'warning',
    // CRITICAL, alone among the wear findings. Below 1.6 mm the vehicle should not be on
    // the road this morning; every other tyre state in the product is a purchase to plan.
    tyre_illegal: 'critical',
    // Warning rather than critical, and a tenant carrying pharmaceuticals should raise it
    // per rule. The default has to hold for the operator hauling frozen chips, where a
    // confirmed excursion is an expensive morning and not an emergency — and `critical`
    // spent on the common case is `critical` that nobody reads on the rare one.
    temperature_excursion: 'warning',
    // Critical, and the second type in the whole vocabulary to hold it. A person pressed
    // a concealed button. If this is ever downgraded to make a dashboard calmer, the
    // dashboard was the wrong thing to optimise.
    panic: 'critical',
    engine_overheat: 'warning',
    fuel_overconsumption: 'warning',
};
/**
 * RULE 1, EXPRESSED AS NUMBERS. How long one rule stays quiet for one vehicle after
 * it fires, when the rule does not say.
 *
 * The spread is deliberate and it is the difference between a product people keep
 * and one they mute. `ignition_on` on a delivery van fires forty times a day and its
 * default is an hour, so a rule someone switched on out of curiosity cannot flood
 * anybody. `crash` is 60 s — long enough to collapse the two or three records one
 * impact produces, short enough that a second, separate impact minutes later is its
 * own alert, because it is.
 *
 * Zero is not in this table and `shouldFire` treats a zero cooldown as "no cooldown".
 * That is a real configuration — an operator who wants every geofence crossing — and
 * the dedupe key still collapses genuine duplicates.
 */
export const ALERT_DEFAULT_COOLDOWN_S = {
    ignition_on: 3_600,
    ignition_off: 3_600,
    towing: 600,
    geofence_in: 300,
    geofence_out: 300,
    overspeed: 600,
    harsh_event: 900,
    crash: 60,
    jamming: 900,
    unplug: 900,
    power_cut: 900,
    low_vehicle_battery: 21_600,
    low_internal_battery: 21_600,
    fuel_drain: 900,
    fuel_refill: 900,
    excessive_idling: 1_800,
    offline: 21_600,
    maintenance_due: 86_400,
    route_deviation: 1_800,
    unidentified_driver: 3_600,
    door_open_in_zone: 600,
    driver_licence_expiry: 86_400,
    // A day. The condition persists until somebody changes the tyre, and the dedupe key
    // is the reading rather than the sweep — so this bounds the re-fire after a NEW
    // measurement confirms it, not the first one.
    tyre_illegal: 86_400,
    // Half an hour. An excursion PERSISTS — re-firing every five minutes while the load
    // warms up is the flood rule 1 forbids, and staying silent for a day on a running
    // excursion is worse. Thirty minutes is "still broken, still your problem".
    temperature_excursion: 1_800,
    // Sixty seconds, like `crash`: long enough to collapse the two or three records one
    // press produces, short enough that a SECOND press four minutes later is its own
    // alert — because a driver pressing again is telling you the first one did not work.
    panic: 60,
    engine_overheat: 1_800,
    // At most once a day per vehicle: the finding is about a whole day.
    fuel_overconsumption: 86_400,
};
/**
 * The second half of rule 1. A cooldown collapses a condition that flaps at one
 * cadence; a ceiling per rolling hour catches the one that flaps at a cadence just
 * slower than the cooldown — a loose battery terminal producing an `unplug` every
 * eleven minutes with a ten-minute cooldown is twenty notifications a shift, each of
 * them legitimately outside the previous one's cooldown.
 *
 * `crash` and `panic` are exempt below (see `shouldFire`): a vehicle that credibly
 * crashed six times in an hour is a vehicle nobody should stop being told about, and a
 * driver pressing a duress button six times is a driver nobody should stop hearing.
 */
export const ALERT_DEFAULT_MAX_PER_HOUR = 6;
export const ALERT_RATE_WINDOW_MS = 3_600_000;
/**
 * RULE 3. The device channel that raises each event type on an FMC150, or null for
 * the ones no device can know.
 *
 * Keys are registry channels, so a rename in `registry.ts` breaks this table at
 * typecheck rather than producing a rule that silently never matches. The values map
 * onto AVL 246/247/249/251/252/255 and 253/254 — see AVL_ID_REFERENCE.md, which
 * records that the eventual I/O block 236–255 is the source for all of them.
 *
 * A type with a channel here can still be derived platform-side for a device that is
 * not configured to raise it; the ALERT then carries `source: 'platform'` and the UI
 * says so. What must never happen is the two arriving as two alerts for one event,
 * which is what `preferDeviceEvidence` is for.
 */
export const ALERT_DEVICE_CHANNELS = {
    ignition_on: 'ignition',
    ignition_off: 'ignition',
    towing: 'towing',
    geofence_in: null,
    geofence_out: null,
    overspeed: 'over_speeding',
    harsh_event: 'green_driving_type',
    crash: 'crash_detection',
    jamming: 'jamming',
    unplug: 'unplug',
    power_cut: 'external_voltage',
    low_vehicle_battery: 'external_voltage',
    low_internal_battery: 'battery_voltage',
    fuel_drain: null,
    fuel_refill: null,
    excessive_idling: 'idling',
    offline: null,
    maintenance_due: null,
    route_deviation: null,
    unidentified_driver: null,
    door_open_in_zone: 'door_status',
    driver_licence_expiry: null,
    // No device reports tread depth. A gauge and a person do.
    tyre_illegal: null,
    // The FMC150 reports a TEMPERATURE. It has no idea what the load is, what the
    // contract says, or whether the door is open — so the excursion is RIVENA's
    // derivation and the UI must say so.
    temperature_excursion: null,
    // AVL 236, the device's own alarm input. There is no platform-side derivation of
    // this and there must never be one: inferring duress from driving behaviour would
    // produce false alarms that get the real ones ignored.
    panic: 'alarm',
    // Coolant on most programs, engine block on a few: the scan reads both.
    engine_overheat: 'engine_temperature',
    fuel_overconsumption: null,
};
/** True for the types only RIVENA can produce — rule 3's "labelled fallback" list. */
export function isPlatformOnlyAlert(type) {
    return ALERT_DEVICE_CHANNELS[type] === null;
}
/**
 * Given both kinds of evidence for the same occurrence, keep the device's.
 *
 * The platform derivation of a harsh brake cannot see a 0.4 s event in a 1 Hz
 * position series, and the derivation of towing cannot distinguish a tow truck from
 * a ferry. Where the device spoke, it is the better witness — and, more importantly,
 * emitting both produces two alerts for one event, which is rule 1's failure with
 * extra steps.
 *
 * Not a merge: the device event is returned unchanged, so the alert's `source` and
 * its payload stay exactly what the device said.
 */
export function preferDeviceEvidence(candidates) {
    const device = candidates.filter((candidate) => candidate.source === 'device');
    return device.length > 0 ? device : candidates;
}
/* ────────────────────────────────────────────────────────────────────────────
 * Rule 2 — the crash enumeration
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * What AVL 247 actually said.
 *
 *  - `none`    — the element is present and reads 0. Not a crash; the device is
 *                simply reporting the channel.
 *  - `real`    — the device says a real crash. Values 1 (calibrated) and 6 (not).
 *  - `trace`   — a crash TRACE was recorded, limited or full, calibrated or not
 *                (2, 3, 4, 5). The device captured accelerometer data around an
 *                event it did not classify as a real crash. Evidence, not an
 *                emergency.
 *  - `fake`    — the device explicitly classified it as NOT a crash: 7 is a pothole,
 *                8 is a speed-check bump. RAISING ONE OF THESE AS A CRASH IS THE
 *                SINGLE WORST THING THIS MODULE COULD DO, because the third phantom
 *                ambulance is how a fleet stops answering the phone.
 *  - `unknown` — a value this table does not know.
 */
export const CRASH_OUTCOMES = ['none', 'real', 'trace', 'fake', 'unknown'];
const CRASH_LABELS = {
    none: { fr: 'Aucun choc', en: 'No crash' },
    real: { fr: 'Choc réel', en: 'Real crash' },
    trace: { fr: 'Trace de choc enregistrée', en: 'Crash trace recorded' },
    fake: { fr: 'Faux choc (nid-de-poule / ralentisseur)', en: 'Fake crash (pothole / speed bump)' },
    unknown: { fr: 'Valeur de choc inconnue', en: 'Unrecognised crash value' },
};
/**
 * The enumeration is transcribed from the `crash_detection` channel in
 * `registry.ts`, which is the platform's single copy of AVL 247's value set;
 * `alerts.test.ts` asserts the two agree, so a firmware revision that adds a value is
 * a red test rather than a silent misclassification.
 *
 * ─── WHY AN UNKNOWN VALUE STILL RAISES, AT WARNING ──────────────────────────
 *
 * The two failure modes point opposite ways here and neither is free. Dropping an
 * unrecognised value silently means a firmware that renumbers the enum turns off
 * crash alerting with no symptom. Raising it as `critical` means a renumbered "fake"
 * value dispatches an ambulance to a pothole. So it raises at `warning`, labelled as
 * unrecognised: somebody sees it the same day, nobody is dispatched, and the
 * registry test above is what turns it back into a real crash alert.
 */
export function assessCrash(value) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return {
            value: null,
            outcome: 'none',
            calibrated: null,
            raise: false,
            severity: 'info',
            label: CRASH_LABELS.none,
        };
    }
    const numeric = Math.trunc(value);
    switch (numeric) {
        case 0:
            return {
                value: numeric,
                outcome: 'none',
                calibrated: null,
                raise: false,
                severity: 'info',
                label: CRASH_LABELS.none,
            };
        case 1:
        case 6:
            return {
                value: numeric,
                outcome: 'real',
                calibrated: numeric === 1,
                raise: true,
                severity: 'critical',
                label: CRASH_LABELS.real,
            };
        case 2:
        case 3:
        case 4:
        case 5:
            return {
                value: numeric,
                outcome: 'trace',
                // 3 and 5 are the calibrated traces, 2 and 4 the uncalibrated ones.
                calibrated: numeric === 3 || numeric === 5,
                raise: true,
                severity: 'warning',
                label: CRASH_LABELS.trace,
            };
        case 7:
        case 8:
            return {
                value: numeric,
                outcome: 'fake',
                calibrated: null,
                // The whole point of rule 2. The device examined it and said no.
                raise: false,
                severity: 'info',
                label: CRASH_LABELS.fake,
            };
        default:
            return {
                value: numeric,
                outcome: 'unknown',
                calibrated: null,
                raise: true,
                severity: 'warning',
                label: CRASH_LABELS.unknown,
            };
    }
}
/**
 * Complete enough to act on? A crash alert whose snapshot has neither a speed nor an
 * ignition state is a crash alert that says only "something happened", and the UI has
 * to be able to say so rather than render blanks that read as zeroes.
 */
export function snapshotIsActionable(snapshot) {
    if (snapshot === null)
        return false;
    return snapshot.speedKph !== null || snapshot.ignition !== null;
}
/* ────────────────────────────────────────────────────────────────────────────
 * Rule 6 — scope and schedule
 * ──────────────────────────────────────────────────────────────────────────── */
export const ALERT_SCOPE_KINDS = ['all', 'vehicles', 'fleets'];
/**
 * An EMPTY id list matches nothing, and that is the safe direction.
 *
 * The alternative reading — an empty list means "no restriction" — turns a rule whose
 * vehicles were all deleted into a tenant-wide rule, which is how a fleet that
 * scoped a "night use" alert to two vans starts getting it for two hundred.
 */
export function scopeIncludes(scope, target) {
    switch (scope.kind) {
        case 'all':
            return true;
        case 'vehicles':
            return (scope.vehicleIds ?? []).includes(target.vehicleId);
        case 'fleets': {
            const fleetIds = scope.fleetIds ?? [];
            return target.fleetIds.some((fleetId) => fleetIds.includes(fleetId));
        }
        default:
            return false;
    }
}
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;
export function parseClock(value) {
    const match = HH_MM.exec(value);
    if (match === null)
        return null;
    return Number(match[1]) * 60 + Number(match[2]);
}
/**
 * THE TIMEZONE BUG THIS FILE EXISTS TO PREVENT, ISOLATED IN ONE FUNCTION.
 *
 * Everything is stored UTC and a schedule is written in local time. Africa/Casablanca
 * is UTC+01 for most of the year and UTC+00 during Ramadan — it is NOT a fixed
 * offset, and the transition dates are not Europe's. A "nuit 22h–6h" rule evaluated
 * against `new Date(ts).getUTCHours()` fires an hour early or an hour late depending
 * on the season, which means a night-use alert that arrives at 21:00 local and misses
 * the vehicle that left at 22:00. That is worse than having no rule at all, because
 * the fleet believes it is covered.
 *
 * `Intl` is the only source in Node that knows about those transitions, and it is the
 * same source `apps/workers/src/trips/windows.ts` uses for day boundaries, so a
 * schedule evaluated here and a day computed there cannot disagree.
 *
 * An invalid zone name throws from `Intl` — deliberately not caught. A tenant row
 * with a typo'd timezone must fail loudly at the first evaluation rather than
 * silently evaluate every schedule in UTC.
 */
export function zonedWallClock(instantMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(new Date(instantMs));
    const field = (type) => {
        const found = parts.find((part) => part.type === type);
        return found === undefined ? 0 : Number(found.value);
    };
    const year = field('year');
    const month = field('month');
    const day = field('day');
    const hour = field('hour');
    const minute = field('minute');
    return {
        year,
        month,
        day,
        hour,
        minute,
        // From the LOCAL calendar date read back as UTC — not from the instant's own
        // UTC weekday, which is a different day for two hours out of every twenty-four.
        weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
        minuteOfDay: hour * 60 + minute,
    };
}
/**
 * Whether an instant falls inside a rule's schedule window, in the tenant's zone.
 *
 * A null schedule means "always", which is the default a rule gets when nobody
 * configured one.
 *
 * THE DAY OF A WRAPPING WINDOW IS THE DAY IT STARTED. "Nuit 22h–6h, lundi" covers
 * Monday 22:00 through Tuesday 05:59, not Monday 00:00–06:00 as well. Getting this
 * backwards is subtle and survives review: both readings look right on a Tuesday.
 */
export function scheduleAllows(schedule, instantMs, timeZone) {
    if (schedule === null || schedule === undefined)
        return true;
    const from = parseClock(schedule.from);
    const to = parseClock(schedule.to);
    // A malformed window is not a licence to fire at any hour: a rule whose schedule
    // cannot be read is a rule whose author's intent is unknown, and firing outside the
    // intended hours is the failure rule 6 names as worse than not firing at all.
    if (from === null || to === null)
        return false;
    const days = schedule.days.length === 0 ? [0, 1, 2, 3, 4, 5, 6] : schedule.days;
    const local = zonedWallClock(instantMs, timeZone);
    if (from < to) {
        return days.includes(local.weekday) && local.minuteOfDay >= from && local.minuteOfDay < to;
    }
    // Wrapped. Before `to` belongs to YESTERDAY's window, so the day that has to be
    // enabled is the previous one.
    if (local.minuteOfDay >= from)
        return days.includes(local.weekday);
    if (local.minuteOfDay < to)
        return days.includes((local.weekday + 6) % 7);
    return false;
}
/** Whether an instant falls inside the organisation's working hours, in its zone. */
export function withinWorkingHours(hours, instantMs, timeZone) {
    return hours.days.some((day) => scheduleAllows({ days: [day.weekday], from: day.from, to: day.to }, instantMs, timeZone));
}
/* ────────────────────────────────────────────────────────────────────────────
 * The rule, the event, and the decision
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Where an alert can be delivered.
 *
 * `push` is the one that justifies a mobile app existing at all. The web app installs
 * on a home screen and does everything else the phone app does; what it cannot do
 * reliably is wake an iPhone at 03:00 for a crash. An alert platform whose alerts do
 * not reach the phone is one people check when they remember to, which is never the
 * night it matters.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'sms', 'push'];
/**
 * The unit of `minValue`/`maxValue` per event type, so the rule editor can label the
 * field and the alert body can print it. `null` means the type takes no threshold.
 */
export const ALERT_CONDITION_UNITS = {
    ignition_on: null,
    ignition_off: null,
    towing: null,
    geofence_in: null,
    geofence_out: null,
    overspeed: 'kph',
    harsh_event: 'g_force',
    crash: null,
    jamming: null,
    unplug: null,
    power_cut: 'volt',
    low_vehicle_battery: 'volt',
    low_internal_battery: 'volt',
    fuel_drain: 'litre',
    fuel_refill: 'litre',
    excessive_idling: 'minute',
    offline: 'minute',
    maintenance_due: 'kilometre',
    route_deviation: 'metre',
    unidentified_driver: 'minute',
    door_open_in_zone: null,
    driver_licence_expiry: null,
    tyre_illegal: 'millimetre',
    temperature_excursion: 'celsius',
    // No threshold. A button is pressed or it is not, and offering an operator a number
    // to tune here would invite them to tune it upward.
    panic: null,
    engine_overheat: 'celsius',
    fuel_overconsumption: 'percent',
};
const CONFIDENCE_RANK = { low: 0, medium: 1, high: 2 };
export const ALERT_DECISION_REASONS = [
    'ok',
    'wrong_event_type',
    'rule_disabled',
    'out_of_scope',
    'geofence_mismatch',
    'below_threshold',
    'above_threshold',
    'low_confidence',
    'crash_not_actionable',
    'outside_schedule',
    'within_working_hours',
    'no_working_hours',
    'rate_limited',
    'in_cooldown',
];
export function dedupeKeyFor(input) {
    const subject = input.vehicleId ?? 'fleet';
    const discriminator = input.discriminator ?? null;
    if (discriminator !== null && discriminator !== '') {
        return `${input.ruleId}:${input.eventType}:${subject}:id=${discriminator}`;
    }
    const windowS = input.cooldownS > 0 ? input.cooldownS : 1;
    const bucket = Math.floor(input.occurredAtMs / (windowS * 1000));
    return `${input.ruleId}:${input.eventType}:${subject}:w=${bucket}`;
}
export function cooldownFor(rule) {
    return rule.cooldownS ?? ALERT_DEFAULT_COOLDOWN_S[rule.eventType];
}
export function severityFor(rule, event) {
    if (event.type === 'crash') {
        const assessment = assessCrash(event.crashValue);
        // A trace never inherits a rule's `critical`: rule 2 says the three outcomes are
        // treated differently, and a rule author raising the severity of crash alerts
        // meant real ones.
        if (assessment.outcome !== 'real')
            return assessment.severity;
    }
    return rule.severity ?? ALERT_DEFAULT_SEVERITY[rule.eventType];
}
/**
 * THE DECISION. Ordered cheapest-and-most-decisive first, and the order is part of
 * the contract because `reason` is what the diagnostics panel shows: a rule that is
 * both disabled and out of scope should say `rule_disabled`, which is the thing its
 * author can act on.
 */
export function shouldFire(rule, event, context) {
    const cooldownS = cooldownFor(rule);
    const severity = severityFor(rule, event);
    const dedupeKey = dedupeKeyFor({
        ruleId: rule.id,
        eventType: event.type,
        vehicleId: event.vehicleId,
        occurredAtMs: event.occurredAtMs,
        cooldownS,
        discriminator: event.discriminator ?? null,
    });
    const deny = (reason) => ({
        fire: false,
        reason,
        severity,
        dedupeKey,
        source: event.source,
    });
    if (rule.eventType !== event.type)
        return deny('wrong_event_type');
    if (!rule.enabled)
        return deny('rule_disabled');
    if (event.vehicleId !== null &&
        !scopeIncludes(rule.scope, {
            vehicleId: event.vehicleId,
            fleetIds: context.fleetIds ?? [],
        })) {
        return deny('out_of_scope');
    }
    // A rule pinned to a zone fires only for events in it. The event's own zone wins
    // when it has one (a geofence transition knows exactly which zone it crossed); we
    // fall back to where the vehicle currently is for the types that have no zone of
    // their own, such as `door_open_in_zone` on a device that reports doors but not
    // zones.
    if (rule.geofenceId !== null) {
        const eventZone = event.geofenceId ?? null;
        const inside = context.insideGeofenceIds ?? [];
        const matches = eventZone === null ? inside.includes(rule.geofenceId) : eventZone === rule.geofenceId;
        if (!matches)
            return deny('geofence_mismatch');
    }
    if (event.type === 'crash') {
        const assessment = assessCrash(event.crashValue);
        // Rule 2, and the single most important line in this file: 7 and 8 are the
        // device telling us it was a pothole or a speed check. They never become alerts.
        if (!assessment.raise)
            return deny('crash_not_actionable');
        if (assessment.outcome === 'trace' && rule.conditions.includeCrashTraces !== true) {
            return deny('crash_not_actionable');
        }
    }
    const value = event.value ?? null;
    if (rule.conditions.minValue !== undefined) {
        if (value === null || !Number.isFinite(value) || value < rule.conditions.minValue) {
            return deny('below_threshold');
        }
    }
    if (rule.conditions.maxValue !== undefined) {
        if (value === null || !Number.isFinite(value) || value > rule.conditions.maxValue) {
            return deny('above_threshold');
        }
    }
    if (rule.conditions.minConfidence !== undefined) {
        const confidence = event.confidence ?? null;
        if (confidence === null ||
            CONFIDENCE_RANK[confidence] < CONFIDENCE_RANK[rule.conditions.minConfidence]) {
            return deny('low_confidence');
        }
    }
    // Rule 6. Against the EVENT's instant, not `now`: an alert about something that
    // happened at 23:10 is a night alert even if the evaluation runs at 00:05.
    if (!scheduleAllows(rule.schedule, event.occurredAtMs, context.timeZone)) {
        return deny('outside_schedule');
    }
    if (rule.conditions.outsideWorkingHours === true) {
        const hours = context.workingHours ?? null;
        if (hours === null)
            return deny('no_working_hours');
        if (withinWorkingHours(hours, event.occurredAtMs, context.timeZone)) {
            return deny('within_working_hours');
        }
    }
    // Crash and duress are exempt from both throttles. A vehicle that credibly crashed
    // twice in ninety seconds has rolled, and the second alert is the one that says so.
    // A driver pressing the button again is telling you the first press did not reach
    // anybody — throttling THAT is the single worst thing this engine could do.
    if (event.type !== 'crash' && event.type !== 'panic') {
        const maxPerHour = rule.maxPerHour ?? ALERT_DEFAULT_MAX_PER_HOUR;
        if (maxPerHour > 0 && (context.firedInWindow ?? 0) >= maxPerHour)
            return deny('rate_limited');
        const lastFiredAtMs = context.lastFiredAtMs ?? null;
        if (cooldownS > 0 && lastFiredAtMs !== null) {
            const sinceMs = event.occurredAtMs - lastFiredAtMs;
            // `>= 0` guards the out-of-order case: a buffered record OLDER than the last
            // alert is inside the same episode by definition, and re-raising it would date
            // a new notification to yesterday.
            if (sinceMs >= 0 && sinceMs < cooldownS * 1000)
                return deny('in_cooldown');
        }
    }
    return { fire: true, reason: 'ok', severity, dedupeKey, source: event.source };
}
/* ────────────────────────────────────────────────────────────────────────────
 * Rule 5 — geofence transitions, from consecutive fixes
 * ──────────────────────────────────────────────────────────────────────────── */
export const GEOFENCE_TRANSITIONS = ['enter', 'exit', 'none'];
/**
 * A transition is a CHANGE, and a change needs two fixes. The PostGIS predicate
 * (`ST_Covers` / `ST_DWithin` for a corridor) decides `inside` for each fix; this
 * decides what the pair MEANS.
 *
 * `previousInside === null` — no previous fix, or one the platform never evaluated —
 * yields `none`, not `enter`. A vehicle whose first-ever fix is inside a zone did not
 * enter it while we were watching, and a device coming back from a week offline would
 * otherwise emit an entry for every zone it happens to be parked in.
 *
 * AND A VEHICLE THAT WAS NEVER INSIDE CANNOT EXIT. That is the same statement read
 * the other way, and it is the one that matters for buffered records: an out-of-order
 * flush whose "previous" fix is actually the later one must not fabricate a crossing.
 * The caller is responsible for feeding fixes in RECORD-TIMESTAMP order — see
 * `orderedByRecordTime` below, which is how the shell proves it did.
 */
export function geofenceTransition(previousInside, currentInside) {
    if (previousInside === null)
        return 'none';
    if (!previousInside && currentInside)
        return 'enter';
    if (previousInside && !currentInside)
        return 'exit';
    return 'none';
}
/** True when a series is non-decreasing in record time. */
export function orderedByRecordTime(timestampsMs) {
    for (let index = 1; index < timestampsMs.length; index += 1) {
        if (timestampsMs[index] < timestampsMs[index - 1])
            return false;
    }
    return true;
}
/* ────────────────────────────────────────────────────────────────────────────
 * Rule 4 — the offline watchdog, and its summary
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The threshold a vehicle has to be silent for. Twenty minutes, not the projection's
 * ten: `LIVE_OFFLINE_AFTER_MS` colours a marker grey on a map somebody is looking at,
 * and being wrong there costs nothing. This one sends a notification.
 */
export const OFFLINE_DEFAULT_AFTER_S = 20 * 60;
/**
 * Past this many vehicles in one sweep, the watchdog emits ONE alert instead of N.
 *
 * RULE 4, AND THE NUMBER IS LOW ON PURPOSE. A GSM outage in one city, an operator
 * changing an APN, a power cut at a depot where forty vans are parked — every one of
 * those makes a block of vehicles go quiet within one window, and none of them is
 * forty separate incidents. Five is small enough that a genuine cluster is summarised
 * and large enough that a real "these four trucks died" is still four alerts a
 * dispatcher can act on individually.
 */
export const OFFLINE_SUMMARY_MIN_VEHICLES = 5;
/** How many vehicles the summary names. The rest are a count. */
export const OFFLINE_SUMMARY_SAMPLE_SIZE = 10;
/**
 * Rule 4. A timer, not an event — and explicitly designed for the 500-at-once case.
 *
 * The summary's `occurredAtMs` is the sweep's own instant and its dedupe key is
 * bucketed by the `offline` cooldown, so a network outage lasting two hours produces
 * one alert every six hours (the default cooldown) rather than one per sweep. The
 * vehicles are still individually offline in the live projection the whole time —
 * `liveConnectivity` answers that at read time, from `last_seen_at`, and it is where
 * the map gets its grey markers. This function only decides who gets NOTIFIED.
 */
export function offlineWatchdog(input) {
    const thresholdS = input.afterS ?? OFFLINE_DEFAULT_AFTER_S;
    const minForSummary = input.summaryMinVehicles ?? OFFLINE_SUMMARY_MIN_VEHICLES;
    const sampleSize = input.sampleSize ?? OFFLINE_SUMMARY_SAMPLE_SIZE;
    const silent = input.vehicles
        .map((vehicle) => ({
        vehicle,
        // A device that never reported is silent since forever. Reported as a very
        // large number rather than null so it sorts to the top of the worst cases,
        // where a fleet manager can see that a newly fitted box never came online.
        silentForS: vehicle.lastSeenAtMs === null
            ? Number.POSITIVE_INFINITY
            : Math.max(0, Math.round((input.nowMs - vehicle.lastSeenAtMs) / 1000)),
    }))
        .filter((entry) => entry.silentForS >= thresholdS)
        .sort((a, b) => b.silentForS - a.silentForS);
    if (silent.length === 0)
        return { kind: 'none' };
    if (silent.length < minForSummary) {
        return {
            kind: 'individual',
            events: silent.map((entry) => ({
                type: 'offline',
                tenantId: input.tenantId,
                vehicleId: entry.vehicle.vehicleId,
                // The instant the vehicle CROSSED the threshold, not the sweep's clock.
                // A sweep that runs every five minutes would otherwise date the same
                // silence differently on every pass, and the dedupe bucket would move with
                // it — turning one dead vehicle into an alert every cooldown window.
                occurredAtMs: thresholdCrossingMs(entry.vehicle, input.nowMs, thresholdS),
                source: 'platform',
                value: Number.isFinite(entry.silentForS) ? Math.round(entry.silentForS / 60) : null,
                payload: {
                    silentForS: Number.isFinite(entry.silentForS) ? entry.silentForS : null,
                    registration: entry.vehicle.registration ?? null,
                    thresholdS,
                },
            })),
        };
    }
    const summary = {
        count: silent.length,
        sample: silent.slice(0, sampleSize).map((entry) => ({
            vehicleId: entry.vehicle.vehicleId,
            registration: entry.vehicle.registration ?? null,
            silentForS: Number.isFinite(entry.silentForS) ? entry.silentForS : -1,
        })),
        longestSilentForS: Number.isFinite(silent[0]?.silentForS ?? 0)
            ? silent[0]?.silentForS
            : -1,
        thresholdS,
    };
    return {
        kind: 'summary',
        summary,
        event: {
            type: 'offline',
            tenantId: input.tenantId,
            // No single vehicle: this alert is about a fleet-wide condition, and
            // attributing it to the first vehicle in the list would put a mass outage on
            // one van's history forever.
            vehicleId: null,
            occurredAtMs: input.nowMs,
            source: 'platform',
            value: silent.length,
            payload: { ...summary },
        },
    };
}
function thresholdCrossingMs(vehicle, nowMs, thresholdS) {
    if (vehicle.lastSeenAtMs === null)
        return nowMs;
    return vehicle.lastSeenAtMs + thresholdS * 1000;
}
/* ────────────────────────────────────────────────────────────────────────────
 * The stored alert, its workflow, and the in-app transport
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * RULE 7. Not a boolean.
 *
 * `open → acknowledged → resolved`, and an alert may be escalated at any point
 * without leaving its status — escalation is a flag on the row and an entry in the
 * timeline, because "who acknowledged the crash alert, when, and what did they say"
 * is a question asked in a dispute months later, and a status column cannot answer
 * it.
 */
export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'];
export const ALERT_TIMELINE_KINDS = [
    'created',
    'notified',
    'notification_failed',
    'acknowledged',
    'comment',
    'escalated',
    'resolved',
    'reopened',
];
/**
 * The legal moves. `resolved → open` exists because an alert closed by mistake has to
 * be reopenable — with a timeline entry saying who reopened it — rather than
 * duplicated by a second alert that loses the first one's history.
 */
const STATUS_TRANSITIONS = {
    open: ['acknowledged', 'resolved'],
    acknowledged: ['resolved', 'open'],
    resolved: ['open'],
};
export function canTransition(from, to) {
    return STATUS_TRANSITIONS[from].includes(to);
}
/** The timeline entry a status change must write. Rule 7: every move is recorded. */
export function timelineKindForTransition(from, to) {
    if (to === 'acknowledged')
        return 'acknowledged';
    if (to === 'resolved')
        return 'resolved';
    return from === 'resolved' ? 'reopened' : 'created';
}
/**
 * RULE 8's in-app half. The worker publishes here; the EXISTING socket.io gateway
 * relays it into `WS_EVENTS.event`.
 *
 * A separate Redis channel from `fleetcore:live:events` on purpose: live-state
 * envelopes are high-frequency and lossy by design (the publisher coalesces and drops
 * the oldest), and an alert must never be dropped because a fleet was moving fast.
 */
export const ALERT_EVENT_CHANNEL = 'fleetcore:live:alerts';
/** Bumped when the envelope's shape changes; a reader drops what it cannot read. */
export const ALERT_EVENT_VERSION = 1;
export function isAlertEnvelope(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (candidate.v === ALERT_EVENT_VERSION &&
        typeof candidate.tenantId === 'string' &&
        typeof candidate.alertId === 'string' &&
        typeof candidate.eventType === 'string' &&
        ALERT_EVENT_TYPES.includes(candidate.eventType) &&
        typeof candidate.severity === 'string' &&
        ALERT_SEVERITIES.includes(candidate.severity));
}
//# sourceMappingURL=alerts.js.map